import { FormEvent, useEffect, useState } from 'react';
import { SEOHead } from '../../components/seo/SEOHead';
import { Input, Label, Textarea } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { api } from '../../lib/api';

interface Ticket {
  id: string;
  subject: string;
  message: string;
  status: string;
  created_at: string;
}

export default function SupportTickets() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [form, setForm] = useState({ subject: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  function loadTickets() {
    api.support
      .list()
      .then((res) => setTickets(res.tickets as Ticket[]))
      .catch(() => setTickets([]));
  }

  useEffect(() => {
    loadTickets();
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setStatus('submitting');
    try {
      await api.support.create(form);
      setForm({ subject: '', message: '' });
      setStatus('success');
      loadTickets();
    } catch {
      setStatus('error');
    }
  }

  return (
    <>
      <SEOHead title="Support — Bharat Consultancy" description="" canonicalPath="/dashboard/support" />

      <h1 className="font-display text-2xl font-bold text-ink">Support</h1>

      <form onSubmit={handleSubmit} className="mt-6 max-w-lg space-y-4 rounded-lg border border-line bg-white p-5">
        <h2 className="font-display text-base font-semibold text-ink">New Support Request</h2>
        <div>
          <Label htmlFor="subject">Subject</Label>
          <Input id="subject" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
        </div>
        <div>
          <Label htmlFor="message">Message</Label>
          <Textarea
            id="message"
            rows={3}
            required
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
          />
        </div>
        <Button type="submit" disabled={status === 'submitting'}>
          {status === 'submitting' ? 'Submitting...' : 'Submit Request'}
        </Button>
        {status === 'success' && <p className="text-sm text-ledger-green">Request submitted.</p>}
        {status === 'error' && <p className="text-sm text-red-600">Could not submit your request. Please try again.</p>}
      </form>

      <div className="mt-8 space-y-3">
        <h2 className="font-display text-base font-semibold text-ink">Your Requests</h2>
        {tickets.length === 0 ? (
          <p className="text-sm text-ink/60">No support requests yet.</p>
        ) : (
          tickets.map((ticket) => (
            <div key={ticket.id} className="rounded-lg border border-line bg-white p-4">
              <div className="flex items-center justify-between">
                <p className="font-medium text-ink">{ticket.subject}</p>
                <span className="rounded-full bg-ink/5 px-2.5 py-0.5 text-xs text-ink/60">{ticket.status}</span>
              </div>
              <p className="mt-1 text-sm text-ink/60">{ticket.message}</p>
            </div>
          ))
        )}
      </div>
    </>
  );
}
